import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './store/auth'
import { Layout } from './components/Layout'
import { UnauthorizedAlert } from './components/UnauthorizedAlert'
import { RequireModule } from './components/RequireModule'
import { EnergyOSDashboardLayout } from './components/EnergyOSDashboardLayout'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { LandingPage } from './pages/LandingPage'
import { EnergyOSLanding } from './pages/EnergyOSLanding'
import { EnergyOSSignup } from './pages/EnergyOSSignup'
import { EnergyOSHomeownerDashboard } from './pages/EnergyOSHomeownerDashboard'
import { EnergyOSDeviceDetail } from './pages/EnergyOSDeviceDetail'
import { EnergyOSHomeDetail } from './pages/EnergyOSHomeDetail'
import { Users } from './pages/Users'
import { Roles } from './pages/Roles'
import { Paths } from './pages/Paths'
import { Events } from './pages/Events'
import { Subscriptions } from './pages/Subscriptions'
import { ApiKeys } from './pages/ApiKeys'
import { Mailboxes } from './pages/Mailboxes'
import { Emails } from './pages/Emails'
import { Datasets } from './pages/Datasets'
import { DatasetDetail } from './pages/DatasetDetail'
import { Documents } from './pages/Documents'
import { AuditLogs } from './pages/AuditLogs'
import { ServiceSettings } from './pages/ServiceSettings'
import { VerifyEmail } from './pages/VerifyEmail'
import { BrainerCollections } from './pages/BrainerCollections'
import { BrainerIngredients } from './pages/BrainerIngredients'
import { BrainerTraits } from './pages/BrainerTraits'
import { BrainerCups } from './pages/BrainerCups'
import { BrainerSteps } from './pages/BrainerSteps'
import { BrainerPreparations } from './pages/BrainerPreparations'
import { BrainerPreparationDetail } from './pages/BrainerPreparationDetail'
import { BrainerCupDetail } from './pages/BrainerCupDetail'
import { RolePaths } from './pages/RolePaths'
import { BrainerFunnels } from './pages/BrainerFunnels'
import { BrainerFunnelCatalog } from './pages/BrainerFunnelCatalog'
import { BrainerFunnelWizard } from './pages/BrainerFunnelWizard'
import { BrainerFunnelDetail } from './pages/BrainerFunnelDetail'
import { BrainerFunnelEdit } from './pages/BrainerFunnelEdit'
import { BrainerDrinkers } from './pages/BrainerDrinkers'
import { BrainerPlates } from './pages/BrainerPlates'
import { BrainerPlateDetail } from './pages/BrainerPlateDetail'
import { BrainerPlateRunner } from './pages/BrainerPlateRunner'
import { ChooseApp } from './pages/ChooseApp'
import { PlateViewerPage } from './components/PlateViewerPage'
import { OpenPlaceLayout } from './components/OpenPlaceLayout'
import { OpenPlaceTokenView } from './pages/OpenPlaceTokenView'
import { LensScenarios } from './pages/LensScenarios'
import { LensScenarioDetail } from './pages/LensScenarioDetail'
import { LensRunDetail } from './pages/LensRunDetail'
import { LensDataSources } from './pages/LensDataSources'
import { LensDataSourceDetail } from './pages/LensDataSourceDetail'
import { PdfTemplates } from './pages/PdfTemplates'
import { PdfTemplateDetail } from './pages/PdfTemplateDetail'
import { Invoices } from './pages/Invoices'
import { Modules } from './pages/Modules'
import { GarageHome } from './pages/GarageHome'
import { GarageDashboardLayout } from './components/GarageDashboardLayout'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

// Bare "/" shows a public landing page to anonymous visitors instead of bouncing straight to
// /login — every other path is untouched (still redirects to /login when logged out). Logged-in
// users hitting "/" fall through to Layout as before.
function RootGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  if (isAuthenticated) return <>{children}</>
  if (location.pathname === '/') return <LandingPage />
  return <Navigate to="/login" replace />
}

export default function App() {
  return (
    <>
    <UnauthorizedAlert />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify/:token" element={<VerifyEmail />} />
      <Route path="/o/:token" element={<OpenPlaceTokenView />} />
      <Route path="/energy-os" element={<EnergyOSLanding />} />
      <Route path="/energy-os/signup" element={<EnergyOSSignup />} />

      <Route
        path="/energy-os/dashboard"
        element={
          <RequireAuth>
            <RequireModule module="energy-os">
              <EnergyOSDashboardLayout />
            </RequireModule>
          </RequireAuth>
        }
      >
        <Route index element={<EnergyOSHomeownerDashboard />} />
        <Route path="devices/:id" element={<EnergyOSDeviceDetail />} />
        <Route path="home" element={<EnergyOSHomeDetail />} />
      </Route>

      <Route
        path="/"
        element={
          <RootGate>
            <Layout />
          </RootGate>
        }
      >
        <Route index element={<Navigate to="/users" replace />} />
        <Route path="users" element={<Users />} />
        <Route path="roles" element={<Roles />} />
        <Route path="roles/:roleId/paths" element={<RolePaths />} />
        <Route path="paths" element={<Paths />} />
        <Route path="events" element={<Events />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="mailboxes" element={<RequireModule module="mail-service"><Mailboxes /></RequireModule>} />
        <Route path="emails" element={<RequireModule module="mail-service"><Emails /></RequireModule>} />
        <Route path="invoices" element={<RequireModule module="invoice-facteur"><Invoices /></RequireModule>} />
        <Route path="datasets" element={<RequireModule module="storage-service"><Datasets /></RequireModule>} />
        <Route path="datasets/:id" element={<RequireModule module="storage-service"><DatasetDetail /></RequireModule>} />
        <Route path="documents" element={<RequireModule module="storage-service"><Documents /></RequireModule>} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="settings" element={<ServiceSettings />} />
        <Route path="modules" element={<Modules />} />
        <Route path="brainer/collections" element={<RequireModule module="brainer"><BrainerCollections /></RequireModule>} />
        <Route path="brainer/ingredients" element={<RequireModule module="brainer"><BrainerIngredients /></RequireModule>} />
        <Route path="brainer/traits" element={<RequireModule module="brainer"><BrainerTraits /></RequireModule>} />
        <Route path="brainer/cups" element={<RequireModule module="brainer"><BrainerCups /></RequireModule>} />
        <Route path="brainer/cups/:id" element={<RequireModule module="brainer"><BrainerCupDetail /></RequireModule>} />
        <Route path="brainer/steps" element={<RequireModule module="brainer"><BrainerSteps /></RequireModule>} />
        <Route path="brainer/drinkers" element={<RequireModule module="brainer"><BrainerDrinkers /></RequireModule>} />
        <Route path="brainer/preparations" element={<RequireModule module="brainer"><BrainerPreparations /></RequireModule>} />
        <Route path="brainer/preparations/:id" element={<RequireModule module="brainer"><BrainerPreparationDetail /></RequireModule>} />
        <Route path="brainer/coffees" element={<Navigate to="/brainer/cups" replace />} />
<Route path="brainer/funnels" element={<RequireModule module="brainer"><BrainerFunnels /></RequireModule>} />
        <Route path="brainer/funnels/catalog" element={<RequireModule module="brainer"><BrainerFunnelCatalog /></RequireModule>} />
        <Route path="brainer/funnels/new" element={<RequireModule module="brainer"><BrainerFunnelWizard /></RequireModule>} />
        <Route path="brainer/funnels/:id" element={<RequireModule module="brainer"><BrainerFunnelDetail /></RequireModule>} />
        <Route path="brainer/funnels/:id/edit" element={<RequireModule module="brainer"><BrainerFunnelEdit /></RequireModule>} />
        <Route path="brainer/plates" element={<RequireModule module="brainer"><BrainerPlates /></RequireModule>} />
        <Route path="brainer/plates/:id" element={<RequireModule module="brainer"><BrainerPlateDetail /></RequireModule>} />
        <Route path="brainer/plates/:id/run" element={<RequireModule module="brainer"><BrainerPlateRunner /></RequireModule>} />
        <Route path="lens/scenarios" element={<RequireModule module="lens"><LensScenarios /></RequireModule>} />
        <Route path="lens/scenarios/:id" element={<RequireModule module="lens"><LensScenarioDetail /></RequireModule>} />
        <Route path="lens/runs/:id" element={<RequireModule module="lens"><LensRunDetail /></RequireModule>} />
        <Route path="lens/datasources" element={<RequireModule module="lens"><LensDataSources /></RequireModule>} />
        <Route path="lens/datasources/:id" element={<RequireModule module="lens"><LensDataSourceDetail /></RequireModule>} />
        <Route path="pdf-templates" element={<RequireModule module="storage-service"><PdfTemplates /></RequireModule>} />
        <Route path="pdf-templates/:id" element={<RequireModule module="storage-service"><PdfTemplateDetail /></RequireModule>} />
      </Route>

      {/* Garage's own dedicated workspace — no Rabbit Console chrome, see GarageDashboardLayout's
          own comment. Its whole content area is whatever Plate is currently routed: "/garage"
          resolves and redirects to the business's "Garage — Home" plate, "/garage/plates/:id"
          renders any Garage plate (including ones forwarded to from Home's nav cards) cleanly. */}
      <Route
        path="/garage"
        element={
          <RequireAuth>
            <RequireModule module="garage">
              <GarageDashboardLayout />
            </RequireModule>
          </RequireAuth>
        }
      >
        <Route index element={<GarageHome />} />
        <Route path="plates/:id" element={<PlateViewerPage />} />
      </Route>

      <Route path="/choose-app" element={<RequireAuth><ChooseApp /></RequireAuth>} />

      <Route
        path="/open"
        element={<RequireAuth><OpenPlaceLayout /></RequireAuth>}
      >
        <Route path=":id" element={<PlateViewerPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  )
}
